import React, { useMemo } from 'react';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import { useTranslation } from 'react-i18next';
import { useLoaderData } from 'react-router-dom';
import { DraftFunction } from 'use-immer';

import Location from '@r6digital/rapidstor-types/react/entities/Location';

import { LoqateAddressFindResponseItem, LoqateValidationFields } from '@/types/Loqate';
import { WizardValidationError } from '@/types/Wizard';
import { getMoveInDateRange, RapidStorSettings } from '@/utils/rapidstor';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import ErrorTag from '@/components/elements/error-tag';

import classes from './styles.module.scss';

interface LoqateAddressResults {
    main: LoqateAddressFindResponseItem[];
    alternate: LoqateAddressFindResponseItem[];
}

interface Props {
    form: Record<string, string>;
    update: (key: string, e: React.BaseSyntheticEvent) => void;
    updateNeededDate: (e: Date) => void;
    updateDate: (key: string, date: Date) => void;
    updateForm: (
        arg: Record<string, string> | DraftFunction<Record<string, string>>
    ) => void;
    alternate?: boolean;
    validationErrors?: WizardValidationError[];
    loqateValidation: (field: string, value: string, alternate?: boolean) => void;
    loqateLoading?: LoqateValidationFields;
    loqateAddressResults?: LoqateAddressResults;
    setLoqateAddressResults?: (results: LoqateAddressResults) => void;
    setAddressUsingLoqate?: (address: LoqateAddressFindResponseItem, alternate?: boolean) => void;
}

export default function ContactDetailsForm({
    form,
    update,
    updateNeededDate,
    updateForm,
    updateDate,
    alternate,
    validationErrors,
    loqateValidation,
    loqateLoading,
    loqateAddressResults,
    setLoqateAddressResults,
    setAddressUsingLoqate,
}: Props) {
    const location = useLoaderData() as Location;
    const { t } = useTranslation();

    const dateRange = useMemo(() => getMoveInDateRange(location), [location]);

    const setTenantCommercial = (value: string) => {
        updateForm((draft) => {
            draft['tenant_commercial'] = value;
        });
    };

    const loqateResults = useMemo(() => {
        if (!loqateAddressResults) {
            return [];
        }

        if (alternate) {
            return loqateAddressResults.alternate ?? [];
        } else {
            return loqateAddressResults.main ?? [];
        }
    }, [alternate, loqateAddressResults]);

    const formErrors = useMemo(() => {
        let inputNames: string[] = [];
        if (alternate) {
            inputNames = [
                'firstName_alternate',
                'lastName_alternate',
                'phone_alternate',
                'email_alternate',
                'address_alternate',
                'city_alternate'
            ];

            if (RapidStorSettings(location).state().show()) inputNames.push('state_alternate');
            if (RapidStorSettings(location).postalCode().show()) inputNames.push('postcode_alternate');
        } else {
            inputNames = [
                'firstName',
                'lastName',
                'phone',
                'email',
                'address',
                'city',
                form.tenant_commercial === 'true' ? 'company' : '',
                form.tenant_commercial === 'true' ? 'companyNumber' : '',
            ];

            if (RapidStorSettings(location).dateOfBirth().show()) inputNames.push('dateOfBirth');
            if (RapidStorSettings(location).state().show()) inputNames.push('state');
            if (RapidStorSettings(location).postalCode().show()) inputNames.push('postcode');
        }

        return validationErrors?.filter((error) => inputNames.includes(error.name));
    }, [alternate, validationErrors, location, form.tenant_commercial]);

    return (
        <div className={`${classes.form}`}>
            {!alternate &&
                RapidStorSettings(location).customerType().show() && (
                    <div className={`${classes.field} ${classes.inline}`}>
                        <div
                            className={`${classes.box}`}
                            onClick={() => setTenantCommercial('false')}
                        >
                            <span>{t('wizard:personal')}</span>
                            <div className={`${classes.custom_checkbox}`}>
                                <input
                                    id='tenant_personal'
                                    type='checkbox'
                                    checked={form.tenant_commercial === 'false'}
                                    onChange={() => setTenantCommercial('false')}
                                />
                                <label htmlFor='tenant_personal'>
                                    <FontAwesomeIcon
                                        style={{
                                            maxHeight: '1rem'
                                        }}
                                        icon="check"
                                    />
                                </label>
                            </div>
                        </div>

                        <div
                            className={`${classes.box}`}
                            onClick={() => setTenantCommercial('true')}
                        >
                            <span>{t('wizard:business')}</span>
                            <div className={`${classes.custom_checkbox}`}>
                                <input
                                    id='tenant_commercial'
                                    type='checkbox'
                                    checked={form.tenant_commercial === 'true'}
                                    onChange={() => setTenantCommercial('true')}
                                />
                                <label htmlFor='tenant_commercial'>
                                    <FontAwesomeIcon
                                        style={{
                                            maxHeight: '1rem'
                                        }}
                                        icon="check"
                                    />
                                </label>
                            </div>
                        </div>
                    </div>
                )}

            {!alternate && (
                <div className={`${classes.field}`}>
                    <label>{t('wizard:selectDate')}</label>
                    <DatePicker
                        showIcon
                        selected={moment(form.neededDate).toDate()}
                        toggleCalendarOnIconClick={true}
                        minDate={dateRange?.min}
                        maxDate={dateRange?.max}
                        required
                        onFocus={(e) => {
                            e.currentTarget.blur();
                        }}
                        excludeDates={location.rapidstor.datesClosed
                            .split(',')
                            .map((d) => {
                                return moment(d, 'DD-MM-YYYY').toDate();
                            })}
                        disabledKeyboardNavigation={true}
                        dateFormat={location.rapidstor.dateFormat}
                        onChange={(e: Date | null) => {
                            if (e) {
                                updateNeededDate(e)
                            }
                        }}
                        className={`${classes.datepicker}`}
                    />
                </div>
            )}

            <div className={`${classes.field}`}>
                {alternate ? (
                    <>
                        <span className={`${classes.alternate_title}`}>
                            {t('wizard:alternateContactDetails:title')}
                        </span>
                        <span className={`${classes.alternate_subtitle}`}>
                            {t('wizard:alternateContactDetails:subtitle')}
                        </span>
                    </>
                ) : (
                    <span className={`${classes.header_text}`}>
                        {t('wizard:contactDetails:title')}
                    </span>
                )}

                {formErrors?.filter(val => val.name != 'dateOfBirth').map((error) => (
                    <ErrorTag key={`${error.name}_error_idx`} error={error} />
                ))}

                <div className={`${classes.contact_row_1}`}>
                    <select
                        value={alternate ? form.title_alternate : form.title}
                        onChange={(e) =>
                            update(alternate ? 'title_alternate' : 'title', e)
                        }
                    >
                        <option value={t('wizard:contactDetails:titles:mr')}>
                            {t('wizard:contactDetails:titles:mr')}
                        </option>
                        <option value={t('wizard:contactDetails:titles:mrs')}>
                            {t('wizard:contactDetails:titles:mrs')}
                        </option>
                        <option value={t('wizard:contactDetails:titles:miss')}>
                            {t('wizard:contactDetails:titles:miss')}
                        </option>
                        <option value={t('wizard:contactDetails:titles:ms')}>
                            {t('wizard:contactDetails:titles:ms')}
                        </option>
                        <option value={t('wizard:contactDetails:titles:dr')}>
                            {t('wizard:contactDetails:titles:dr')}
                        </option>
                    </select>

                    <input
                        type='text'
                        placeholder={t('contact_card:first_name')}
                        value={alternate ? form.firstName_alternate : form.firstName}
                        onChange={(e) =>
                            update(alternate ? 'firstName_alternate' : 'firstName', e)
                        }
                    />

                    <input
                        type='text'
                        placeholder={t('contact_card:last_name')}
                        value={alternate ? form.lastName_alternate : form.lastName}
                        onChange={(e) =>
                            update(alternate ? 'lastName_alternate' : 'lastName', e)
                        }
                    />
                </div>

                {!alternate && form.tenant_commercial === 'true' && (
                    <div className={`${classes.contact_row_commercial}`}>
                        <span>{t('wizard:contactDetails:commercial')}</span>
                        <div className={`${classes.inner}`}>
                            <input
                                type='text'
                                placeholder={t('wizard:contactDetails:company')}
                                value={form.company}
                                onChange={(e) => update('company', e)}
                            />
                            <input
                                type='text'
                                placeholder={t('wizard:contactDetails:companyNumber')}
                                value={form.companyNumber}
                                onChange={(e) => update('companyNumber', e)}
                            />
                        </div>
                    </div>
                )}

                <div className={`${classes.contact_row_2}`}>
                    <input
                        type='tel'
                        placeholder={t('contact_card:phone')}
                        value={alternate ? form.phone_alternate : form.phone}
                        onChange={(e) =>
                            update(alternate ? 'phone_alternate' : 'phone', e)
                        }
                    />

                    <input
                        type='email'
                        placeholder={t('contact_card:email')}
                        value={alternate ? form.email_alternate : form.email}
                        className={`${!alternate &&
                            validationErrors?.find((item) => item.name === 'email') &&
                            classes.invalid
                            }`}
                        disabled={!alternate && loqateLoading === 'email'}
                        onBlur={(e) => {
                            if (!alternate) {
                                loqateValidation('email', e.currentTarget.value);
                            }
                        }}
                        onChange={(e) =>
                            update(alternate ? 'email_alternate' : 'email', e)
                        }
                    />
                </div>
                <div className={`${classes.contact_row_3}`}>
                    {loqateResults.length > 0 && (
                        <div className={`${classes.autocomplete_popup}`}>
                            {loqateResults.map((address) => (
                                <span
                                    key={`${address.Id}_address_idx`}
                                    onClick={() => {
                                        if (
                                            setLoqateAddressResults &&
                                            setAddressUsingLoqate
                                        ) {
                                            setAddressUsingLoqate(address, alternate);
                                            setLoqateAddressResults({ main: [], alternate: [] });
                                        }
                                    }}
                                >
                                    {`${address.Text} - ${address.Description}`}
                                </span>
                            ))}
                        </div>
                    )}

                    <input
                        type='text'
                        placeholder={t('contact_card:address')}
                        value={alternate ? form.address_alternate : form.address}
                        disabled={!alternate && loqateLoading === 'address'}
                        onChange={(e) => {
                            update(alternate ? 'address_alternate' : 'address', e);
                            if (!e.currentTarget.value && setLoqateAddressResults) {
                                setLoqateAddressResults({ main: [], alternate: [] });
                            } else {
                                if (e.currentTarget.value.length > 4) {
                                    loqateValidation('address', e.currentTarget.value, alternate);
                                }
                            }
                        }}
                    />

                    <input
                        type='text'
                        placeholder={t('contact_card:city')}
                        disabled={!alternate && loqateLoading === 'address'}
                        value={alternate ? form.city_alternate : form.city}
                        onChange={(e) => {
                            update(alternate ? 'city_alternate' : 'city', e);
                            if (!e.currentTarget.value && setLoqateAddressResults) {
                                setLoqateAddressResults({ main: [], alternate: [] });
                            } else {
                                loqateValidation('city', e.currentTarget.value, alternate);
                            }
                        }}
                    />

                    {RapidStorSettings(location).state().show() && (
                        <input
                            type='text'
                            placeholder={t('contact_card:state')}
                            disabled={!alternate && loqateLoading === 'address'}
                            value={alternate ? form.state_alternate : form.state}
                            onChange={(e) => {
                                update(alternate ? 'state_alternate' : 'state', e);
                                if (
                                    !e.currentTarget.value &&
                                    setLoqateAddressResults
                                ) {
                                    setLoqateAddressResults({ main: [], alternate: [] });
                                } else {
                                    loqateValidation('state', e.currentTarget.value, alternate);
                                }
                            }}
                        />
                    )}

                    {RapidStorSettings(location).postalCode().show() && (
                        <input
                            type='text'
                            placeholder={t('contact_card:postcode')}
                            disabled={!alternate && loqateLoading === 'address'}
                            value={alternate ? form.postcode_alternate : form.postcode}
                            onChange={(e) => {
                                update(alternate ? 'postcode_alternate' : 'postcode', e);
                                if (
                                    !e.currentTarget.value &&
                                    setLoqateAddressResults
                                ) {
                                    setLoqateAddressResults({ main: [], alternate: [] });
                                } else {
                                    if (e.currentTarget.value.length >= 2) {
                                        loqateValidation(
                                            'postcode',
                                            e.currentTarget.value,
                                            alternate
                                        );
                                    }
                                }
                            }}
                        />
                    )}
                </div>

                {RapidStorSettings(location).dateOfBirth().show() && !alternate && (
                    <>
                        {formErrors?.filter(val => val.name === 'dateOfBirth').map((error) => (
                            <ErrorTag key={`${error.name}_error_idx`} error={error} />
                        ))}

                        <div className={`${classes.field}`}>
                            <label>{t('wizard:dateOfBirth')}</label>
                            <DatePicker
                                toggleCalendarOnIconClick={true}
                                showIcon
                                required
                                showMonthDropdown
                                showYearDropdown
                                dropdownMode='select'
                                maxDate={new Date()}
                                onChange={(e) => {
                                    if (e) {
                                        updateDate('dateOfBirth', e);
                                    }
                                }}
                                value={
                                    form.dateOfBirth
                                        ? moment(form.dateOfBirth).format('DD/MM/YYYY')
                                        : ''
                                }
                                selected={form.dateOfBirth ? new Date(form.dateOfBirth) : null}
                                placeholderText='Date of Birth'
                                className={`${classes.datepicker}`}
                            />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
